<template>
    <section class="storyfinder-persona-selector">
        <div class="container-fluid mx-auto">
            <div class="ps-hero">
                <svg xmlns="http://www.w3.org/2000/svg" width="714" height="432" viewBox="0 0 714 432" fill="none">
                    <path d="M3.17578 479.175C22.6546 396.241 77.2321 329.767 147.246 303.738C217.26 277.71 301.202 292.682 368.83 343.261C378.679 244.952 419.109 155.253 481.277 93.9193C543.435 32.5676 626.856 0.0202019 713.176 3.41646" stroke="#E8DDC9" stroke-width="6.35" stroke-miterlimit="10" stroke-linecap="round">
                    </path>
                </svg>
                <div
                    v-if="heroImage"
                    class="ps-hero-bg"
                    :style="{ backgroundImage: `url(${heroImage})` }"
                ></div>
                <div class="ps-hero-overlay"></div>
                <div class="ps-hero-content">
                    <p class="ps-question">Welke gids mag<br>meedenken over jouw perfecte<br>bezoek aan onze
                        regio?</p>
                </div>
            </div>

            <div class="ps-bottom">
                <h3 class="ps-header-title">Kies jouw gids</h3>
                <div class="ps-personas-track" ref="track">
                    <div
                        v-for="persona in personas"
                        :key="persona.key"
                        class="ps-persona-card"
                    >
                        <div class="ps-card-media">
                            <img
                                v-if="persona.imageUrl"
                                :src="persona.imageUrl"
                                :alt="persona.label"
                                class="ps-card-image"
                                loading="lazy"
                            />
                            <div v-else class="ps-card-placeholder" aria-hidden="true"></div>
                        </div>
                        <div class="ps-card-body">
                            <div class="ps-card-texts">
                                <span class="ps-name">{{ persona.label }}</span>
                                <span v-if="persona.tagline" class="ps-tagline">{{ persona.tagline }}</span>
                            </div>
                            <button
                                type="button"
                                class="ps-card-btn"
                                @click="selectPersona(persona.key)"
                            >
                                Ik kies {{ persona.label }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
export default {
    name: 'PersonaSelector',
    props: {
        personas: {
            type: Array,
            required: true
        },
        currentPersona: {
            type: String,
            default: null
        },
        cookieName: {
            type: String,
            required: true
        },
        wizardUrl: {
            type: String,
            default: '/verhaalvinder/wizard'
        },
        heroImage: {
            type: String,
            default: '/sites/default/files/2026-08/Najaarscampagne%20nieuwsbrief.jpg'
        }
    },
    data() {
        return {
            activePersona: this.currentPersona || (this.personas.length ? this.personas[0].key : null)
        };
    },
    mounted() {
        this.$nextTick(this.scrollToActive);
    },
    methods: {
        scrollToActive() {
            if (!this.$refs.track) return;
            const activeCard = this.$refs.track.querySelector('.is-active');
            if (!activeCard) return;
            const track = this.$refs.track;
            const scrollTo = activeCard.offsetLeft - (track.offsetWidth / 2) + (activeCard.offsetWidth / 2);
            track.scrollLeft = scrollTo;
        },
        selectPersona(key) {
            this.activePersona = key;
            this.$cookie.set(this.cookieName, key, {expires: 180, path: '/'});
            window.location.href = this.wizardUrl;
        }
    }
};
</script>

<style scoped lang="scss">
$color-taupe: #e8ddc9;
$color-yellow: #e5bb5f;
$color-black: #23201e;
$color-white: #ffffff;
$font-family: "Comfortaa", sans-serif;

.storyfinder-persona-selector {
    font-family: $font-family;
    background-color: $color-taupe;
    overflow: hidden;
}

// ── Hero ──────────────────────────────────────────────────────────────────────

.ps-hero {
    position: relative;
    min-height: 520px;
    max-width: 1400px;
    margin: 0 auto;
    background-color: $color-black;
    overflow: hidden;
    border-radius: 1.5rem;
    isolation: isolate;
    grid-column: 1;
    grid-row: 1;
    z-index: 0;

    &:before {
        content: "";
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        top: 0;
        bottom: 0;
        width: 100vw;
        background: linear-gradient(210deg, rgba(35, 32, 30, 0.6) 5.69%, rgba(35, 32, 30, 0.1) 34.18%, rgba(35, 32, 30, 0.1) 52.62%, rgba(35, 32, 30, 0.4) 78.15%);
        z-index: 1;
    }

    svg {
        position: absolute;
        z-index: 10;
        bottom: 0;
        right: 0;
        max-width: 50%;
        height: auto;
    }
}

.ps-hero-bg {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
}

.ps-hero-overlay {
    position: absolute;
    inset: 0;
}

.ps-hero-content {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    z-index: 9;
}

.ps-question {
    font-weight: 700;
    font-size: 3rem;
    line-height: 3.5rem;
    color: #fff;
    text-align: center;
    margin: 0;

    @media (max-width: 768px) {
        font-size: 1.8rem;
        line-height: 2.2rem;
    }
}

// ── Bottom / Cards Track ──────────────────────────────────────────────────────

.ps-header-title {
    font-weight: 700;
    font-size: 2rem;
    line-height: 2.5rem;
    color: $color-black;
    text-align: center;
    margin: 0 0 1.5rem;

    @media (max-width: 768px) {
        font-size: 1.5rem;
        line-height: 2rem;
    }
}

.ps-bottom {
    background-color: $color-taupe;
    padding: 2.5rem 0 3rem;
    max-width: 1400px;
    margin: 0 auto;
}

.ps-personas-track {
    display: flex;
    gap: 24px;
    overflow-x: auto;
    padding: 12px 15px;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    justify-content: flex-start;

    @media (min-width: 992px) {
        justify-content: center;
    }

    &::-webkit-scrollbar {
        display: none;
    }
}

.ps-persona-card {
    flex-shrink: 0;
    width: 280px;
    max-width: 85vw;
    display: flex;
    flex-direction: column;
    border-radius: 16px;
    background-color: $color-white;
    overflow: hidden;
    scroll-snap-align: center;
    box-shadow: 0 4px 14px rgba(35, 32, 30, 0.08);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &.is-active {
        box-shadow: 0 6px 20px rgba(35, 32, 30, 0.15);
        outline: 2px solid $color-yellow;
    }
}

.ps-card-media {
    position: relative;
    width: 100%;
    height: 220px;
    background-color: #d6ccb8;
    overflow: hidden;
}

.ps-card-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.ps-card-placeholder {
    width: 100%;
    height: 100%;
    background-color: #d6ccb8;
}

.ps-card-body {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex: 1;
    padding: 20px;
}

.ps-card-texts {
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
}

.ps-name {
    font-weight: 700;
    font-size: 22px;
    line-height: 1.2;
    color: $color-black;
    margin: 0 0 6px;
}

.ps-tagline {
    font-weight: 500;
    font-size: 14px;
    line-height: 1.4;
    color: rgba($color-black, 0.7);
    margin: 0;
}

.ps-card-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 12px 18px;
    background-color: $color-black;
    color: $color-white;
    font-family: $font-family;
    font-size: 15px;
    font-weight: 700;
    border: none;
    border-radius: 24px;
    cursor: pointer;
    transition: background-color 0.2s ease, transform 0.1s ease;

    &:hover {
        background-color: lighten($color-black, 15%);
    }

    &:focus-visible {
        outline: 2px solid $color-yellow;
        outline-offset: 2px;
    }
}
</style>
